import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Linking,
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';

import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import type { PaperData, ProjectPaperContext } from './types';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

const CLI_DOCS_URL = 'https://docs.paperzilla.ai/guides/cli';
const COPY_FEEDBACK_DURATION_MS = 2000;

type PaperCliPromoVariant = 'inline' | 'spotlight';

interface PaperCliPromoProps {
    paper: PaperData;
    projectPaperContext?: ProjectPaperContext;
    canUseRecommendationCommand?: boolean;
    recommendationTargetId?: string | null;
    variant?: PaperCliPromoVariant;
}

async function copyToClipboard(text: string): Promise<void> {
    if (Platform.OS === 'web') {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return;
            } catch {
                // Fall back to execCommand if Clipboard API access is blocked.
            }
        }

        if (typeof document !== 'undefined') {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            textarea.style.pointerEvents = 'none';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            const copied = document.execCommand('copy');
            document.body.removeChild(textarea);

            if (copied) {
                return;
            }
        }

        throw new Error('Clipboard write failed. Please allow clipboard access and try again.');
    }

    const Clipboard = require('expo-clipboard');
    await Clipboard.setStringAsync(text);
}

export default function PaperCliPromo({
    paper,
    projectPaperContext,
    canUseRecommendationCommand = false,
    recommendationTargetId = null,
    variant = 'inline',
}: PaperCliPromoProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const [copied, setCopied] = useState(false);
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resolvedRecommendationTargetId = recommendationTargetId || (
        canUseRecommendationCommand
            ? (projectPaperContext?.projectPaperShortId || projectPaperContext?.projectPaperId || null)
            : null
    );
    const cliCommand = useMemo(() => (
        resolvedRecommendationTargetId
            ? `pz rec ${resolvedRecommendationTargetId}`
            : `pz paper ${paper.short_id || paper.id}`
    ), [paper.id, paper.short_id, resolvedRecommendationTargetId]);
    const promoLabel = resolvedRecommendationTargetId
        ? i18n._(msg({ id: 'paper.cli.recommendationPrompt', message: 'Get this recommendation in your agent:' }))
        : i18n._(msg({ id: 'paper.cli.paperPrompt', message: 'Get this paper in your agent:' }));

    const clearCopyTimeout = useCallback(() => {
        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => clearCopyTimeout, [clearCopyTimeout]);

    const handleCopyCommand = useCallback(async () => {
        try {
            await copyToClipboard(cliCommand);
            clearCopyTimeout();
            setCopied(true);
            copyTimeoutRef.current = setTimeout(() => {
                setCopied(false);
                copyTimeoutRef.current = null;
            }, COPY_FEEDBACK_DURATION_MS);
        } catch (error) {
            console.error('[PaperCliPromo] Failed to copy CLI command:', error);
            Alert.alert(
                i18n._(msg({ id: 'paper.cli.copyFailedTitle', message: 'Copy failed' })),
                i18n._(msg({ id: 'paper.cli.copyFailedMessage', message: 'Unable to copy the CLI command right now.' }))
            );
        }
    }, [clearCopyTimeout, cliCommand, i18n]);

    const handleOpenDocs = useCallback(async () => {
        try {
            await Linking.openURL(CLI_DOCS_URL);
        } catch {
            Alert.alert(
                i18n._(msg({ id: 'paper.cli.docsOpenFailedTitle', message: 'Unable to open docs' })),
                i18n._(msg({ id: 'paper.cli.docsOpenFailedMessage', message: 'Please visit docs.paperzilla.ai/guides/cli.' }))
            );
        }
    }, [i18n]);

    const rootTestId = variant === 'spotlight'
        ? 'paper-cli-promo-spotlight'
        : 'paper-cli-promo-inline';

    return (
        <View style={styles.compactCard} testID={rootTestId}>
            <View style={styles.headerRow}>
                <View style={styles.titleWrap}>
                    <FontAwesome6 name="terminal" size={12} color={colors.text500} />
                    <Text style={[styles.baseTitle, fontsLoaded && interSemiBold]}>
                        {promoLabel}
                    </Text>
                </View>
            </View>

            <View style={styles.commandRail} testID="paper-cli-promo-command">
                <Text style={styles.commandText}>{cliCommand}</Text>
                <TouchableOpacity
                    style={[styles.copyButton, copied && styles.copyButtonSuccess]}
                    onPress={handleCopyCommand}
                    activeOpacity={0.82}
                    accessibilityLabel={copied
                        ? i18n._(msg({ id: 'paper.cli.commandCopied', message: 'CLI command copied' }))
                        : i18n._(msg({ id: 'paper.cli.copyCommand', message: 'Copy CLI command' }))}
                    testID="paper-cli-promo-copy"
                >
                    <FontAwesome6
                        name={copied ? 'check' : 'copy'}
                        size={13}
                        color={copied ? '#ffffff' : colors.text600}
                    />
                </TouchableOpacity>
            </View>

            <TouchableOpacity
                style={styles.docsLinkRow}
                onPress={handleOpenDocs}
                activeOpacity={0.74}
                testID="paper-cli-promo-docs"
            >
                <FontAwesome6 name="caret-right" size={10} color={colors.text500} />
                <Text style={[styles.docsLinkText, fontsLoaded && interRegular]}><Trans id="papers.paperclipromo.donTHaveTheLatestCli" comment="UI copy in components/papers/PaperCliPromo.tsx">
                    Don&apos;t have the latest CLI?
                </Trans></Text>
            </TouchableOpacity>
        </View>
    );
}

const monoFont = Platform.select({
    web: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    ios: 'Courier',
    android: 'monospace',
    default: 'monospace',
});

const styles = StyleSheet.create({
    compactCard: {
        gap: 7,
        borderRadius: 16,
        paddingHorizontal: 12,
        paddingVertical: 11,
        borderWidth: 1,
        borderColor: '#e3e7ee',
        backgroundColor: '#f3f5f8',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    titleWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
    },
    baseTitle: {
        color: colors.text500,
        fontSize: 13,
        lineHeight: 18,
    },
    commandRail: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#d9dfe8',
        backgroundColor: '#ffffff',
        paddingLeft: 12,
        paddingRight: 6,
        paddingVertical: 6,
        minHeight: 44,
    },
    commandText: {
        flex: 1,
        fontFamily: monoFont,
        fontSize: 15,
        lineHeight: 20,
        color: colors.text900,
    },
    copyButton: {
        width: 32,
        height: 32,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e4e8ef',
        backgroundColor: '#fafbfc',
        alignItems: 'center',
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    copyButtonSuccess: {
        backgroundColor: colors.primaryGreenDark,
        borderColor: colors.primaryGreenDark,
    },
    docsLinkRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        alignSelf: 'flex-start',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    docsLinkText: {
        fontSize: 11,
        lineHeight: 15,
        color: colors.text500,
    },
});
